import type {
  QuoteType,
  DistributionChannel,
  QuoteStatus,
  PolicyState,
  ConcurrencyWithBenefits,
  YesNoChoice,
  IncludeLmsChoice,
  PlanType,
  AggregateBenefit,
  LengthOfLeaveWeeks,
} from './types';

// API Endpoints
export const QUOTES_ENDPOINTS = {
  LIST: '/api/quotes/',
  DETAIL: (id: number) => `/api/quotes/${id}/`,
  CREATE: '/api/quotes/',
  UPDATE: (id: number) => `/api/quotes/${id}/`,
  DELETE: (id: number) => `/api/quotes/${id}/`,
  ADD_NOTE: (id: number) => `/api/quotes/${id}/add-note/`,
  UPDATE_OUTPUT: (quoteId: number, outputId: number) => `/api/quotes/${quoteId}/outputs/${outputId}/`,
  CREATE_NEW_VERSION: (id: number) => `/api/quotes/${id}/create-new-version/`,
  GENERATE_ANALYSIS_FILE: (id: number) => `/api/quotes/${id}/generate-analysis-file/`,
  DOWNLOAD_POWER_POINT_PRESENTATION: (id: number) => `/api/quotes/${id}/download-power-point-presentation/`,
  SYNC_POWER_POINT_PRESENTATION_FROM_ONEDRIVE: (id: number) =>
    `/api/quotes/${id}/sync-power-point-presentation-from-onedrive/`,
  CREATED_BY_USERS: '/api/quotes/created-by-users/',
  RATER_FILES: '/api/rater-files/',
  POWER_POINT_TEMPLATES: '/api/power-point-templates/',
  PEO_GROUPS: '/api/peo-groups/',
  REINSURANCE_PARTNERS: '/api/reinsurance-partners/',
} as const;

// Options for Select Fields
export const QUOTE_TYPE_OPTIONS: Array<{ value: QuoteType; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'renewal', label: 'Renewal' },
];

export const DISTRIBUTION_CHANNEL_OPTIONS: Array<{ value: DistributionChannel; label: string }> = [
  { value: 'direct', label: 'Direct' },
  { value: 'broker', label: 'Broker' },
];

export const QUOTE_STATUS_OPTIONS: Array<{ value: QuoteStatus; label: string }> = [
  { value: 'drafted', label: 'Drafted' },
  { value: 'pricing_review_needed', label: 'Pricing Review Needed' },
  { value: 'approved', label: 'Approved' },
  { value: 'proposal_submitted', label: 'Proposal Submitted' },
  { value: 'expired_outdated_census', label: 'Expired – Outdated Census' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

export const POLICY_STATE_OPTIONS: Array<{ value: PolicyState; label: string }> = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

export const BENEFITS_OPTIONS = [
  '50%',
  '55%',
  '60%',
  '65%',
  '66.67%',
  '70%',
  '75%',
  '80%',
  '85%',
  '90%',
  '95%',
  '100%',
];

export const AGGREGATE_BENEFIT_DEFAULT: AggregateBenefit = '500000';

export const ADMIN_ROLE_TITLE = 'Admin';

export const LENGTH_OF_LEAVE_WEEKS_OPTIONS = ['6', '8', '10', '12', '14', '16'];

export const LENGTH_OF_LEAVE_WEEKS_SEC_OPTIONS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'];

export const LENGTH_OF_SERVICE_OPTIONS = ['0 mo', '3 mo', '6 mo', '9 mo', '12 mo'];

export const CONCURRENCY_OPTIONS: Array<{ value: ConcurrencyWithBenefits; label: string }> = [
  { value: 'concurrently', label: 'Concurrently' },
  { value: 'non-concurrently', label: 'Non-Concurrently' },
];

export const YES_NO_OPTIONS: Array<{ value: YesNoChoice; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// PRTO-8: Include LMS. "Both" is recorded on the quote but does not fan out the
// output matrix yet — the rater has no LMS input.
export const INCLUDE_LMS_OPTIONS: Array<{ value: IncludeLmsChoice; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'both', label: 'Both' },
];

export const PLAN_TYPE_OPTIONS: Array<{ value: PlanType; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: 'primary_and_secondary', label: 'Primary and Secondary' },
];

export const AGGREGATE_BENEFIT_OPTIONS: Array<{ value: AggregateBenefit; label: string }> = [
  { value: '500000', label: '$500,000' },
  { value: '1000000', label: '$1,000,000' },
  { value: '2000000', label: '$2,000,000' },
  { value: '3000000', label: '$3,000,000' },
  { value: '5000000', label: '$5,000,000' },
  { value: 'unlimited', label: 'Unlimited' },
];

export const LENGTH_OF_LEAVE_WEEKS_SELECT_OPTIONS: Array<{ value: LengthOfLeaveWeeks; label: string }> = [
  { value: '6', label: '6' },
  { value: '8', label: '8' },
  { value: '10', label: '10' },
  { value: '12', label: '12' },
  { value: '14', label: '14' },
  { value: '16', label: '16' },
];

// File Upload Constants
export const ALLOWED_CENSUS_EXTENSIONS = ['xls', 'xlsx', 'csv'];
export const MAX_CENSUS_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Status Color Mapping
export const STATUS_COLORS: Record<QuoteStatus, string> = {
  drafted: 'gray',
  pricing_review_needed: 'yellow',
  approved: 'green',
  proposal_submitted: 'blue',
  expired_outdated_census: 'dark-gray',
  closed_won: 'blue',
  closed_lost: 'dark-gray',
};

// Reinsurance Partner → Policy Issuance Fee.
// Mirrors the rater formula in OUT_Policy_Issuance_Fee:
//   = IF(OUT_QUOTED_REIN_PARTNER = List!$H$3, 250, 0)
// where List!$H$3 = "Convex" (List!$H$2 = "Greenlight" is the default).
// So Convex → $250, Greenlight (and everything else) → $0. Centralize the
// lookup here so when more partners get added we update one place; everything
// downstream (Policy Issuance Fee, Quoted Premium With Taxes, Tax/Stamping/
// Issuance, Total Program Costs) follows automatically through
// recalculateOutputFields.
export const POLICY_ISSUANCE_FEE_BY_PARTNER: Record<string, number> = {
  Convex: 250,
};
export const POLICY_ISSUANCE_FEE_DEFAULT = 0;

